import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { companiesAPI } from '../../services/api';
import { FiBriefcase, FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiPhone, FiMapPin, FiGlobe } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Auth.css';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get('role') || 'candidate';

  const [step, setStep] = useState(1);
  const [role, setRole] = useState(initialRole);
  const [showPass, setShowPass] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', password_confirmation: '',
  });

  const [company, setCompany] = useState({
    name: '', description: '', website: '', industry: '', location: '', size: '', email: '', phone: '',
  });

  const update = (field, value) => setForm({ ...form, [field]: value });
  const updateCompany = (field, value) => setCompany({ ...company, [field]: value });

  const validateStep1 = () => {
    const errs = {};
    if (!form.first_name) errs.first_name = 'First name is required';
    if (!form.last_name) errs.last_name = 'Last name is required';
    if (!form.email) errs.email = 'Email is required';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 8) errs.password = 'Minimum 8 characters';
    if (form.password !== form.password_confirmation) errs.password_confirmation = 'Passwords do not match';
    if (!termsAccepted) errs.terms = 'You must accept the Terms & Conditions';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateCompany = () => {
    const errs = {};
    if (!company.name) errs.company_name = 'Company name is required';
    if (!company.industry) errs.company_industry = 'Industry is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      if (role === 'recruiter') {
        setStep(2);
      } else {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if (role === 'recruiter' && step === 2 && !validateCompany()) return;
    setLoading(true);
    try {
      // Send role_id: 2 for recruiter, 3 for candidate along with role name
      const roleId = role === 'recruiter' ? 2 : 3;
      const userData = { ...form, role, role_id: roleId };
      const user = await register(userData);

      // If recruiter, create company AFTER registration so we have a valid auth token
      if (role === 'recruiter' && company.name) {
        try {
          // Only send non-empty fields
          const companyPayload = {};
          Object.entries(company).forEach(([key, val]) => {
            if (val && String(val).trim()) companyPayload[key] = String(val).trim();
          });
          await companiesAPI.create(companyPayload);
        } catch (e) {
          toast.error('Account created but company creation failed. You can add it from your dashboard.');
        }
      }

      navigate('/dashboard');
    } catch (e) {
      // toast shown by context
    }
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card register-card animate-fade-in">
        <div className="auth-header">
          <Link to="/" className="auth-brand"><FiBriefcase /> PlacementHunter</Link>
          <h2>{step === 1 ? 'Create Your Account' : 'Company Details'}</h2>
          <p>{step === 1 ? 'Join South Africa\'s dedicated employment platform' : 'Tell us about your company'}</p>
        </div>

        {step === 1 && (
          <>
            {/* Role Selector */}
            <div className="role-selector">
              <button
                type="button"
                className={`role-option ${role === 'candidate' ? 'active' : ''}`}
                onClick={() => setRole('candidate')}
              >
                <FiUser /> I'm looking for work
              </button>
              <button
                type="button"
                className={`role-option ${role === 'recruiter' ? 'active' : ''}`}
                onClick={() => setRole('recruiter')}
              >
                <FiBriefcase /> I'm hiring
              </button>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>First Name</label>
                <div className="input-icon-wrapper">
                  <FiUser className="input-icon" />
                  <input type="text" placeholder="John" value={form.first_name} onChange={(e) => update('first_name', e.target.value)} />
                </div>
                {errors.first_name && <div className="error">{errors.first_name}</div>}
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" placeholder="Doe" value={form.last_name} onChange={(e) => update('last_name', e.target.value)} />
                {errors.last_name && <div className="error">{errors.last_name}</div>}
              </div>
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <div className="input-icon-wrapper">
                <FiMail className="input-icon" />
                <input type="email" placeholder="you@example.com" value={form.email} onChange={(e) => update('email', e.target.value)} />
              </div>
              {errors.email && <div className="error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <div className="input-icon-wrapper">
                <FiPhone className="input-icon" />
                <input type="tel" placeholder="+27 XX XXX XXXX" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Password</label>
                <div className="input-icon-wrapper">
                  <FiLock className="input-icon" />
                  <input type={showPass ? 'text' : 'password'} placeholder="Min 8 characters" value={form.password} onChange={(e) => update('password', e.target.value)} />
                  <button type="button" className="input-icon-right" onClick={() => setShowPass(!showPass)}>
                    {showPass ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {errors.password && <div className="error">{errors.password}</div>}
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" placeholder="Repeat password" value={form.password_confirmation} onChange={(e) => update('password_confirmation', e.target.value)} />
                {errors.password_confirmation && <div className="error">{errors.password_confirmation}</div>}
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                I agree to the <Link to="/terms" target="_blank">Terms &amp; Conditions</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>
              </label>
              {errors.terms && <div className="error">{errors.terms}</div>}
            </div>

            <button type="button" className="btn btn-primary btn-block btn-lg" onClick={handleNext} disabled={loading}>
              {role === 'recruiter' ? 'Next: Company Details' : (loading ? 'Creating Account...' : 'Create Account')}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="form-group">
              <label>Company Name *</label>
              <div className="input-icon-wrapper">
                <FiBriefcase className="input-icon" />
                <input type="text" placeholder="Acme Pty Ltd" value={company.name} onChange={(e) => updateCompany('name', e.target.value)} />
              </div>
              {errors.company_name && <div className="error">{errors.company_name}</div>}
            </div>

            <div className="form-group">
              <label>Industry *</label>
              <select value={company.industry} onChange={(e) => updateCompany('industry', e.target.value)}>
                <option value="">Select industry</option>
                {['Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing', 'Retail', 'Construction', 'Mining', 'Agriculture', 'Telecommunications', 'Legal', 'Marketing', 'Consulting', 'Other'].map(i => (
                  <option key={i} value={i.toLowerCase()}>{i}</option>
                ))}
              </select>
              {errors.company_industry && <div className="error">{errors.company_industry}</div>}
            </div>

            <div className="form-group">
              <label>Company Description</label>
              <textarea rows="3" placeholder="Brief description of your company..." value={company.description} onChange={(e) => updateCompany('description', e.target.value)} />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Website</label>
                <div className="input-icon-wrapper">
                  <FiGlobe className="input-icon" />
                  <input type="url" placeholder="https://company.co.za" value={company.website} onChange={(e) => updateCompany('website', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Company Size</label>
                <select value={company.size} onChange={(e) => updateCompany('size', e.target.value)}>
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501+">501+ employees</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Location</label>
              <div className="input-icon-wrapper">
                <FiMapPin className="input-icon" />
                <input type="text" placeholder="Johannesburg, Gauteng" value={company.location} onChange={(e) => updateCompany('location', e.target.value)} />
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Company Email</label>
                <input type="email" placeholder="hr@company.co.za" value={company.email} onChange={(e) => updateCompany('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Company Phone</label>
                <input type="tel" placeholder="+27 XX XXX XXXX" value={company.phone} onChange={(e) => updateCompany('phone', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" className="btn btn-secondary btn-lg" onClick={() => setStep(1)} style={{ flex: 1 }}>Back</button>
              <button type="button" className="btn btn-primary btn-lg" onClick={handleSubmit} disabled={loading} style={{ flex: 2 }}>
                {loading ? 'Creating Account...' : 'Create Account & Company'}
              </button>
            </div>
          </>
        )}

        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
